import { Badge, makeStyles, tokens } from "@fluentui/react-components";
import { Warning16Filled } from "@fluentui/react-icons";
import type { Richiesta } from "../data/richieste";
import { formatEuro, formatQuantita, formatRelative } from "../lib/format";
import { StatoBadge } from "./StatoBadge";

const useStyles = makeStyles({
  card: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    width: "100%",
    textAlign: "left",
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    cursor: "pointer",
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  top: { display: "flex", alignItems: "flex-start", gap: tokens.spacingHorizontalS },
  title: { flexGrow: 1, fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400 },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  desc: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    overflowWrap: "anywhere",
  },
  flags: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  urgente: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
});

/** `showRichiedente` = vista gestore: mostra anche richiedente e anteprima della descrizione. */
export function RichiestaCard({ r, showRichiedente, onOpen }: { r: Richiesta; showRichiedente?: boolean; onOpen: () => void }) {
  const s = useStyles();
  const f = r.fields;
  return (
    <button type="button" className={s.card} onClick={onOpen}>
      <div className={s.top}>
        <span className={s.title}>{f.Title}</span>
        <StatoBadge stato={f.Stato} />
      </div>
      <span className={s.meta}>
        {[f.Tipo, f.Servizio, f.Categoria, f.Quantita != null ? `× ${formatQuantita(f.Quantita)}` : undefined, f.Valore != null ? formatEuro(f.Valore) : undefined]
          .filter(Boolean)
          .join(" · ")}
      </span>
      {showRichiedente && f.Descrizione && f.Descrizione !== f.Title && <span className={s.desc}>{f.Descrizione}</span>}
      <span className={s.meta}>
        #{r.id} · {formatRelative(r.createdAt)}
        {showRichiedente && r.createdBy?.displayName ? ` · ${r.createdBy.displayName}` : ""}
      </span>
      {(f.Priorita === "Urgente" || f.ExtraBudget) && (
        <span className={s.flags}>
          {f.Priorita === "Urgente" && (
            <span className={s.urgente}>
              <Warning16Filled /> Urgente
            </span>
          )}
          {f.ExtraBudget && (
            <Badge appearance="tint" color="warning" shape="rounded" size="small">
              Extra budget
            </Badge>
          )}
        </span>
      )}
    </button>
  );
}
